import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/navigation';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 pt-20">
        <div className="container-custom py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.publishedAt,
          author: { '@type': 'Organization', name: '草榴社区网址导航' },
          publisher: { '@type': 'Organization', name: '草榴社区网址导航' },
        }}
      />

      <article className="container-custom py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        {/* Article Header */}
        <header className="mt-6 max-w-3xl">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <Link
              to={`/category/${article.category}`}
              className="px-3 py-1 bg-accent-100 text-accent-700 text-sm font-medium rounded-full whitespace-nowrap hover:bg-accent-200 transition-colors duration-200"
            >
              {article.categoryName}
            </Link>
            <span className="text-sm text-foreground-400">{article.publishedAt}</span>
            <span className="text-sm text-foreground-400">{article.readTime} 阅读</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 text-foreground-600 text-xs font-medium rounded-full whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </header>

        {/* Featured Image */}
        <div className="mt-8 rounded-2xl overflow-hidden aspect-[16/9] max-w-4xl">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Article Content */}
        <div className="mt-10 max-w-3xl">
          <div className="prose-custom">
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              {article.excerpt}
            </p>

            <h2 className="text-xl font-bold text-foreground-950 mt-8 mb-4">一、背景介绍</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              随着网络环境的不断变化，许多社区网站的访问域名也在频繁更新。对于用户而言，找到稳定且安全的访问入口至关重要。
              本导航平台致力于为草榴社区用户提供最及时、最准确的网址信息，帮助大家解决访问难题。
            </p>

            <h2 className="text-xl font-bold text-foreground-950 mt-8 mb-4">二、详细说明</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              我们的网址监控系统采用自动化检测与人工验证相结合的方式，每30分钟对所有收录的入口进行一次可用性检测。
              一旦发现某个入口失效，系统会立即标记并进行替换更新，确保导航列表中的每个网址都处于可用状态。
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              对于不同地区的用户，我们提供了多个镜像节点供选择。您可以根据自己所在的地理位置和网络环境，
              选择访问速度最快的入口。通常建议优先选择标记为"推荐"或"极快"的入口。
            </p>

            <h2 className="text-xl font-bold text-foreground-950 mt-8 mb-4">三、操作步骤</h2>
            <ol className="list-decimal list-inside space-y-3 text-base text-foreground-700 leading-relaxed mb-6">
              <li>打开本导航页面，查看最新可用网址列表</li>
              <li>选择一个状态为"正常"的入口链接</li>
              <li>点击链接直接访问，或复制网址到浏览器地址栏</li>
              <li>如果遇到无法访问的情况，尝试其他镜像地址</li>
              <li>如所有入口均无法访问，请参考我们的DNS设置教程</li>
            </ol>

            <h2 className="text-xl font-bold text-foreground-950 mt-8 mb-4">四、注意事项</h2>
            <ul className="list-disc list-inside space-y-3 text-base text-foreground-700 leading-relaxed mb-6">
              <li>请始终通过本导航页提供的网址访问，确保安全性</li>
              <li>定期清理浏览器缓存，避免因缓存问题导致访问失败</li>
              <li>建议收藏本导航页，以便随时获取最新网址</li>
              <li>如发现失效链接，欢迎通过联系方式反馈给我们</li>
              <li>使用代理或VPN时，请选择可靠的服务器节点</li>
            </ul>

            <h2 className="text-xl font-bold text-foreground-950 mt-8 mb-4">五、常见问题</h2>
            <div className="space-y-3 mb-6">
              <div className="p-4 bg-background-100/70 rounded-lg">
                <h3 className="text-sm font-semibold text-foreground-900">Q: 为什么我打不开提供的网址？</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  A: 请先检查您的网络连接是否正常。如果网络正常，尝试切换本页提供的其他镜像地址，或参考我们的DNS修改教程。
                </p>
              </div>
              <div className="p-4 bg-background-100/70 rounded-lg">
                <h3 className="text-sm font-semibold text-foreground-900">Q: 这些网址安全吗？</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  A: 我们收录的所有网址均经过安全检测和真实性验证，确保不会将用户引导至仿冒或钓鱼网站。
                </p>
              </div>
            </div>

            <h2 className="text-xl font-bold text-foreground-950 mt-8 mb-4">六、总结</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              草榴社区网址导航始终以用户需求为中心，持续优化和完善我们的服务。无论是网址更新速度、入口数量还是访问稳定性，
              我们都在不断精进。感谢您的信任与支持，收藏本页，访问不迷路！
            </p>
          </div>
        </div>

        {/* Tags and Share */}
        <div className="mt-10 max-w-3xl pt-6 border-t border-background-200/70">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-foreground-700">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 bg-background-100 text-foreground-600 text-sm rounded-full whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="mt-12 max-w-3xl">
            <h3 className="text-lg font-bold text-foreground-950 mb-4">相关文章</h3>
            <div className="space-y-3">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="flex items-center gap-4 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={rel.image}
                      alt={rel.title}
                      title={rel.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                      {rel.title}
                    </h4>
                    <p className="mt-0.5 text-xs text-foreground-400">
                      {rel.publishedAt} · {rel.readTime}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Back to category */}
        <div className="mt-10 max-w-3xl">
          <Link
            to={`/category/${article.category}`}
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors duration-200"
          >
            <i className="ri-arrow-left-line"></i>
            返回{article.categoryName}
          </Link>
        </div>
      </article>
    </div>
  );
}